import { Link, useParams } from "react-router";
import { Decimal } from "@/domain/money";
import { computeYear } from "@/finance/year";
import { formatEurPlain, formatEurSigned, formatMonthShort, formatMonthTitle, formatPercent } from "@/lib/format";
import { ColumnChart, SERIES_COLORS } from "@/ui/charts";
import { Figure, Panel, PeriodSwitcher } from "@/ui/primitives";
import { useFinanceIndex, useToday } from "../hooks";

export function YearPage() {
  const { year: currentYear } = useToday();
  const param = Number(useParams().year);
  const year = Number.isInteger(param) && param > 1900 && param < 3000 ? param : currentYear;
  const index = useFinanceIndex();
  if (!index) return <p className="text-muted">Caricamento…</p>;

  const y = computeYear(index, year);
  const labels = y.months.map((m) => formatMonthShort(m.month));
  const titles = y.months.map((m) => formatMonthTitle(m.month));
  const budgetVariance = new Decimal(y.totals.budgetVariance);

  return (
    <div className="flex flex-col gap-5">
      <PeriodSwitcher
        label={String(year)}
        previousHref={`/anno/${year - 1}`}
        nextHref={`/anno/${year + 1}`}
        previousLabel={`Anno ${year - 1}`}
        nextLabel={`Anno ${year + 1}`}
      >
        <p className="text-sm text-muted">Per mese contabile</p>
      </PeriodSwitcher>

      <div className="grid grid-cols-2 gap-x-4 gap-y-5 rounded-xl border border-line bg-surface p-4 md:grid-cols-4 md:p-5">
        <Figure label="Entrate" amount={y.totals.income} hint={`stipendio ${formatEurPlain(y.totals.salary)}`} />
        <Figure label="Spese" amount={y.totals.expenses} />
        <Figure label="Risparmio" amount={y.totals.savings} signed hint={`tasso ${formatPercent(y.totals.savingsRate)}`} />
        <Figure label="Saldo a fine anno" amount={y.balance.closing} hint={`inizio ${formatEurPlain(y.balance.opening)} (${formatPercent(y.balance.changeRatio)})`} />
      </div>

      <Panel title="Budget annuale">
        <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
          <dt className="text-muted">Budget spese</dt>
          <dd className="amount text-right">{formatEurPlain(y.totals.budgetExpenses)}</dd>
          <dt className="text-muted">Spese effettive</dt>
          <dd className="amount text-right">{formatEurPlain(y.totals.expenses)}</dd>
          <dt className="font-semibold">{budgetVariance.isNegative() ? "Oltre il budget" : "Sotto il budget"}</dt>
          <dd className={`amount text-right font-semibold ${budgetVariance.isNegative() ? "text-over" : ""}`}>{formatEurPlain(budgetVariance.abs())}</dd>
          <dt className="text-muted">Entrate previste / risparmio previsto</dt>
          <dd className="amount text-right">
            {formatEurPlain(y.totals.budgetIncome)} / {formatEurSigned(y.totals.projectedSavings)}
          </dd>
        </dl>
      </Panel>

      <Panel title="Entrate e spese per mese">
        <ColumnChart
          ariaLabel={`Entrate e spese per mese nel ${year}`}
          labels={labels}
          tooltipTitles={titles}
          series={[
            { key: "income", label: "Entrate", color: SERIES_COLORS.income, values: y.months.map((m) => Number(m.income)) },
            { key: "expense", label: "Spese", color: SERIES_COLORS.expense, values: y.months.map((m) => Number(m.expenses)) },
          ]}
        />
      </Panel>

      <Panel title="Risparmio mensile">
        <p className="mb-2 text-sm text-muted">Sopra lo zero hai risparmiato, sotto hai speso più di quanto è entrato.</p>
        <ColumnChart
          ariaLabel={`Risparmio mensile nel ${year}`}
          labels={labels}
          tooltipTitles={titles}
          series={[{ key: "savings", label: "Risparmio", color: SERIES_COLORS.income, values: y.months.map((m) => Number(m.savings)) }]}
          colorFor={(_, v) => (v < 0 ? SERIES_COLORS.expense : SERIES_COLORS.income)}
        />
      </Panel>

      <Panel title="Confronto tra i mesi">
        <div className="-mx-4 overflow-x-auto px-4">
          <table className="w-full min-w-[34rem] text-sm">
            <thead className="text-left text-muted">
              <tr>
                <th className="py-1.5 font-normal">Mese</th>
                <th className="py-1.5 text-right font-normal">Entrate</th>
                <th className="py-1.5 text-right font-normal">Spese</th>
                <th className="py-1.5 text-right font-normal">Risparmio</th>
                <th className="py-1.5 text-right font-normal">Saldo a fine mese</th>
              </tr>
            </thead>
            <tbody>
              {y.months.map((m) => (
                <tr key={m.month} className={`border-t border-line ${m.hasData ? "" : "text-muted"}`}>
                  <td className="py-1.5">
                    <Link to={`/mese/${m.month}`} className="underline decoration-line underline-offset-4 hover:text-petrol">
                      {formatMonthTitle(m.month)}
                    </Link>
                  </td>
                  <td className="amount py-1.5 text-right">{formatEurPlain(m.income)}</td>
                  <td className="amount py-1.5 text-right">{formatEurPlain(m.expenses)}</td>
                  <td className="amount py-1.5 text-right">{formatEurSigned(m.savings)}</td>
                  <td className="amount py-1.5 text-right">{formatEurPlain(m.closing)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-ink font-semibold">
                <td className="py-1.5">Totale</td>
                <td className="amount py-1.5 text-right">{formatEurPlain(y.totals.income)}</td>
                <td className="amount py-1.5 text-right">{formatEurPlain(y.totals.expenses)}</td>
                <td className="amount py-1.5 text-right">{formatEurSigned(y.totals.savings)}</td>
                <td className="amount py-1.5 text-right">{formatEurPlain(y.balance.closing)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid gap-5 md:grid-cols-2">
        <Panel title="Mesi con più spese">
          <ol className="space-y-1.5 text-sm">
            {y.highestSpendingMonths.slice(0, 3).map((m) => (
              <li key={m.month} className="flex justify-between gap-3">
                <span>{formatMonthTitle(m.month)}</span>
                <span className="amount">{formatEurPlain(m.expenses)}</span>
              </li>
            ))}
          </ol>
          {y.highestSpendingMonths.length === 0 ? <p className="text-sm text-muted">Nessun dato per quest&apos;anno.</p> : null}
        </Panel>

        <Panel title="Medie mensili">
          <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
            <dt className="text-muted">Entrate ({y.averages.monthsWithData} mesi con dati)</dt>
            <dd className="amount text-right">{y.averages.income ? formatEurPlain(y.averages.income) : "—"}</dd>
            <dt className="text-muted">Spese</dt>
            <dd className="amount text-right">{y.averages.expenses ? formatEurPlain(y.averages.expenses) : "—"}</dd>
            <dt className="text-muted">Risparmio</dt>
            <dd className="amount text-right">{y.averages.savings ? formatEurSigned(y.averages.savings) : "—"}</dd>
          </dl>
          <p className="mt-2 text-xs text-muted">
            Il file Excel divide sempre per 12: entrate {formatEurPlain(y.averages.workbookIncome)}, spese {formatEurPlain(y.averages.workbookExpenses)} al mese.
          </p>
        </Panel>
      </div>

      <Panel title="Spese per gruppo">
        <div className="-mx-4 overflow-x-auto px-4">
          <table className="w-full min-w-[30rem] text-sm">
            <thead className="text-left text-muted">
              <tr>
                <th className="py-1.5 font-normal">Gruppo</th>
                <th className="py-1.5 text-right font-normal">Speso</th>
                <th className="py-1.5 text-right font-normal">Budget</th>
                <th className="py-1.5 text-right font-normal">Differenza</th>
              </tr>
            </thead>
            <tbody>
              {y.byGroup.map((g) => (
                <tr key={g.groupId} className="border-t border-line">
                  <td className="py-1.5 pr-2">{g.label}</td>
                  <td className="amount py-1.5 text-right">{formatEurPlain(g.actual)}</td>
                  <td className="amount py-1.5 text-right text-muted">{formatEurPlain(g.budget)}</td>
                  <td className={`amount py-1.5 text-right ${new Decimal(g.variance).isNegative() ? "text-over" : ""}`}>{formatEurSigned(g.variance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-sm text-muted">
          Fisse {formatEurPlain(y.byNature.fixed)}, discrezionali {formatEurPlain(y.byNature.discretionary)}, non classificate{" "}
          {formatEurPlain(y.byNature.unclassified)}.
        </p>
      </Panel>
    </div>
  );
}
